import { action, computed, makeAutoObservable } from 'mobx'
import { Store } from '@stores'
import { ENDPOINTS } from '@constants'

export class SystemStore {
  _store: Store
  loading: boolean
  job: any | null
  job_batch: any | null
  failed_job: any | null
  retrying_failed_job: boolean

  constructor(store: Store) {
    makeAutoObservable(this)
    this._store = store
    this.loading = false
    this.job = null
    this.job_batch = null
    this.failed_job = null
    this.retrying_failed_job = false
  }

  @computed
  get is_loading() {
    return this.loading
  }

  private async safeFetchJson(input: RequestInfo | URL, init?: RequestInit) {
    const res = await fetch(input, {
      headers: { Accept: 'application/json', ...(init?.headers || {}) },
      ...init,
    })

    // Try content-type first
    const contentType = res.headers.get('content-type') || ''

    // Read raw text so we can decide how to parse without throwing
    const text = await res.text()

    if (!text) {
      return { ok: res.ok, status: res.status, data: null }
    }

    // If looks like JSON or explicitly declared as JSON, parse safely
    const looksLikeJson = contentType.includes('application/json') || /^\s*[[{]/m.test(text)
    if (looksLikeJson) {
      try {
        const data = JSON.parse(text)
        return { ok: res.ok, status: res.status, data }
      } catch (e) {
        // Fall through to return null if parsing fails
      }
    }

    // Non-JSON or parse failed
    return { ok: res.ok, status: res.status, data: null }
  }

  @action
  async getJobBatch(id: string | number) {
    this.loading = true
    this.job_batch = null
    try {
      const { data } = await this.safeFetchJson(ENDPOINTS.SYSTEM.JOB_BATCH_SINGLE(String(id)))
      this.job_batch = data
    } finally {
      this.loading = false
    }
    return this.job_batch
  }

  @action
  async getFailedJob(id: string | number) {
    this.loading = true
    this.failed_job = null
    try {
      const { data } = await this.safeFetchJson(ENDPOINTS.SYSTEM.FAILED_JOB_SINGLE(String(id)))
      this.failed_job = data
    } finally {
      this.loading = false
    }
    return this.failed_job
  }

  @action
  async retryFailedJob(id: string | number) {
    this.retrying_failed_job = true
    try {
      const { data } = await this.safeFetchJson(ENDPOINTS.SYSTEM.FAILED_JOB_RETRY(String(id)), { method: 'POST' })
      return data
    } finally {
      this.retrying_failed_job = false
    }
  }
}
